import get from 'lodash/get';
import { action, autorun, makeObservable, observable, reaction } from 'mobx';

import { SOCKET_REFERRAL_BONUS_CREATED } from '../../config/constants';
import { type BonusItem, type DailyBonusData } from '../../dto/bonus.types';
import { getRequest, postRequest } from '../../services/api';
import { ldClient, resolveFlag } from '../../services/launchDarkly';
import { socket } from '../../services/socket';
import type { ApiResponse } from '../../services/types';
import { devError } from '../../utils/log';
import { ONE_LINK_PARAMS_MAP } from '../../utils/marketingHelper';
import { singleFlight } from '../../utils/singleFlight';
import type { CoinType } from '../cointype/CoinTypeStore';
import type { FeatureFetchStatus, FeatureManagerDialogType, IFeatureManagerStore } from '../feature-manager/types';
import { type RootStore } from '../RootStore';
import { Loadable } from '../traits/Loadable';

import type {
  ProviderJackpot,
  ClaimDailyBonusResponseData,
  ClaimReferralBonusResponse,
  FetchBonusNotificationsResponse,
  FetchReferralDataResponse,
  InviteBonusItem,
  BundleItem,
} from './types';

export class RewardsStore extends Loadable implements IFeatureManagerStore {
  rootStore: RootStore;

  constructor(_rootStore: RootStore) {
    super();
    makeObservable(this);
    this.rootStore = _rootStore;

    autorun(() => {
      const isUserAuthorized = this.rootStore.authStore.isAuthenticationFlowDone && this.rootStore.authStore.authorized;
      //TODO: Uncomment this block if you want to handle static jackpots separately
      //socket.on(SOCKET_JACKPOT_UPDATED, this.subscribeToJackpot.bind(this));

      if (isUserAuthorized) {
        socket.on(SOCKET_REFERRAL_BONUS_CREATED, this.subscribeToSocket.bind(this));
      } else {
        socket.off(SOCKET_REFERRAL_BONUS_CREATED, this.subscribeToSocket.bind(this));
      }
    });

    reaction(
      () => this.rootStore.authStore.isAuthenticationFlowDone && this.rootStore.authStore.authorized,
      (isAuthorized) => {
        if (isAuthorized) {
          void ldClient?.waitUntilReady().then(() => {
            if (resolveFlag('promo_codes_feature', true)) {
              void this.fetchPromoCodeEligibility();
            }
          });
        }
      },
    );

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState !== 'visible') {
          return;
        }
        void this.refreshDailyBonusIfStale();
      });
    }
  }

  dialogTypes: FeatureManagerDialogType[] = ['DailyBonus'];

  private openDialogListener: VoidFunction | null = null;

  public setDialogListener(listener: VoidFunction) {
    if (!this.openDialogListener) {
      this.openDialogListener = listener;
    }
  }

  @observable
  welcomeBonusLoading = false;

  @action
  private setWelcomeBonusLoading(loading: boolean) {
    this.welcomeBonusLoading = loading;
  }

  startDialogs() {
    if (this.dailyBonus?.isEntitledToBonus && this.openDialogListener && !this.isDailyBonusShowedAfterLogin) {
      this.setDailyBonusShowedAfterLogin();
      this.openDialogListener();
      return true;
    }

    return false;
  }

  public fetch = singleFlight(async (): Promise<FeatureFetchStatus> => {
    const featureFlag = resolveFlag('daily_bonus_feature', true);
    if (!featureFlag) return 'OFF';

    this.setLoading(true);
    try {
      const isEntitledToBonus = await this.getDailyBonus();
      return isEntitledToBonus ? 'READY' : 'OFF';
    } catch (e) {
      devError('Error fetching rewards');
      return 'ERROR';
    } finally {
      this.setLoading(false);
    }
  });

  private isDailyBonusShowedAfterLogin = false;
  public setDailyBonusShowedAfterLogin() {
    this.isDailyBonusShowedAfterLogin = true;
  }

  @observable
  public isDailyBonusFetched = false;

  @observable
  public isDailyBonusCompleted = false;

  @observable
  public welcomeBonus?: {
    gcAmount: number;
    scAmount: number;
  };

  @observable
  public dailyBonusesList: BonusItem[] = [];

  @observable
  public monthlyBonusesList: BonusItem[] = [];

  @observable
  public dailyBonus: DailyBonusData | null = null;

  @observable
  public jackpot: Map<number, number | string> = new Map();

  @observable
  public fastJackpot: Map<number, number | string> = new Map();

  private static timeOutRef: Map<number, NodeJS.Timeout> = new Map<number, NodeJS.Timeout>();
  private static intervalRef: Map<number, NodeJS.Timeout> = new Map<number, NodeJS.Timeout>();
  private static secIntervalRef: Map<number, NodeJS.Timeout> = new Map<number, NodeJS.Timeout>();

  @observable
  public providersJackpots: Record<number, ProviderJackpot> = {};

  @observable
  public inviteBonusNotification = 0;

  @observable
  public promoCodeEligible = false;

  private bonusNotificationLoading = false;

  @observable
  public isReferralBonusLoading = false;
  @observable
  public numOfUsersInvited = 0;
  @observable
  public friendsQualified = 0;
  @observable
  public referrerUrl = '';
  @observable
  public inviteBonusList: InviteBonusItem[] = [];
  @observable
  public inviteBonusGC = 0;
  @observable
  public inviteBonusSC = 0;
  @observable
  public inviteMinPurchase = 0;
  @observable
  public inviteDefaultGC: number | null = null;
  @observable
  public inviteDefaultSC: number | null = null;

  @action
  private setInviteBonusNotification(num: number) {
    this.inviteBonusNotification = num;

    this.rootStore.gameStore.sendSlotsGameUiMessage(
      {
        type: 'referralBonusNotification',
        payload: {
          referralBonusNotifications: num,
        },
      },
      true,
    );
  }

  @action
  private async fetchPromoCodeEligibility(): Promise<boolean> {
    try {
      const response = await getRequest<ApiResponse<{ isEligible: boolean }>>('/v2/promo-code/has-eligible');
      const { isEligible } = response.data.data;
      this.promoCodeEligible = isEligible;
      return this.promoCodeEligible;
    } catch (e) {
      this.promoCodeEligible = false;
      return false;
    }
  }

  @action
  public async claimReferralBonus(bonusIds: string[]) {
    try {
      let bonusesSum = { scSum: 0, gcSum: 0 };
      if (this.isReferralBonusLoading) {
        return;
      }
      this.isReferralBonusLoading = true;
      const response = await postRequest<ApiResponse<ClaimReferralBonusResponse>>('user/referral/claim-bonus', {
        bonusIds,
      });
      if (response.data.success) {
        this.isReferralBonusLoading = false;
        await this.fetchReferralData();

        if (Array.isArray(response.data.data)) {
          bonusesSum = response.data.data.reduce(
            (acc, item) => {
              (acc.scSum += item.scAmount || 0), (acc.gcSum += item.gcAmount || 0);
              return acc;
            },
            { scSum: 0, gcSum: 0 },
          );
        }
      }
      return bonusesSum;
    } catch (error) {
      this.isReferralBonusLoading = false;
      devError('Error claiming referral bonus', error);
      return null;
    }
  }

  @action
  public async fetchReferralData() {
    try {
      if (this.isReferralBonusLoading) {
        return;
      }
      this.isReferralBonusLoading = true;
      const response = await getRequest<ApiResponse<FetchReferralDataResponse>>('user/referral/by-referrer');
      if (response.data.success) {
        const { data } = response.data;

        this.numOfUsersInvited = data.rows.length;
        const bonusList: InviteBonusItem[] = data.rows
          .filter((f) => f.bonusId !== null)
          .map((item) => ({
            bonusId: item.bonusId,
            gcAmount: item.gcAmount,
            scAmount: item.scAmount,
            date: item.bonusCreatedAt,
            claimed: item.isBonusClaimed,
          }))
          .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        this.inviteBonusList = bonusList;
        this.friendsQualified = bonusList.length;
        const onelinkConfig = resolveFlag<{ url: string } | null>('referral_onelink_url', null);

        if (onelinkConfig?.url) {
          this.referrerUrl = `${onelinkConfig.url}?${ONE_LINK_PARAMS_MAP.utm_campaign}=${data.uniqueId}&${ONE_LINK_PARAMS_MAP.utm_source}=friends`;
        } else {
          this.referrerUrl = `${window.origin}/?utm_campaign=${data.uniqueId}&utm_source=friends`;
        }

        this.inviteBonusGC = data.packageData.gcAmount;
        this.inviteBonusSC = data.packageData.scAmount;
        this.inviteMinPurchase = data.minPurchase;
        this.inviteDefaultGC = data.defaultPackageData?.gcAmount ?? null;
        this.inviteDefaultSC = data.defaultPackageData?.scAmount ?? null;

        this.setInviteBonusNotification(bonusList.filter((f) => !f.claimed).length);
      }
    } catch (error) {
      devError('Error fetching referral data', error);
    }
    this.isReferralBonusLoading = false;
  }

  @action
  public async fetchInviteBonusNotification() {
    const isInviteFriendsEnabled = resolveFlag('invite_friends_enabled', true);

    if (!isInviteFriendsEnabled) {
      this.setInviteBonusNotification(0);
      return;
    }

    try {
      if (this.bonusNotificationLoading) {
        return;
      }

      this.bonusNotificationLoading = true;
      const response = await getRequest<ApiResponse<FetchBonusNotificationsResponse>>(
        'user/referral/bonus-notifications',
      );

      if (response.data.success) {
        const { data } = response.data;
        this.setInviteBonusNotification(data.notificationsCount || 0);
      }
    } catch (error) {
      devError('Error fetching invite bonus notification', error);
    }
    this.bonusNotificationLoading = false;
  }

  @action
  public setDailyBonusCompleted() {
    this.isDailyBonusCompleted = true;
  }

  @action
  public setProviderJackpot(
    providerId: number,
    data: {
      gcAmount: number;
      scAmount: number;
      jackpotType: string;
    },
  ): void {
    this.providersJackpots = {
      ...this.providersJackpots,
      [providerId]: {
        gcAmount: data.gcAmount,
        scAmount: data.scAmount,
        jackpotType: data.jackpotType,
      },
    };
  }

  @action
  public startJackpotCounter(args: { coinType: CoinType; providerId?: number }) {
    const { coinType, providerId: providerIdArg } = args;

    const items: Map<number, number> = new Map();
    const fieldName = coinType === 'GC' ? 'gcAmount' : 'scAmount';

    if (providerIdArg) {
      items.set(providerIdArg, this.providersJackpots[providerIdArg][fieldName]);
    } else {
      Object.entries(this.providersJackpots).forEach(([key, value]) => {
        items.set(Number(key), value[fieldName]);
      });
    }

    //TODO: Uncomment this block if you want to handle static jackpots separately
    // if (args.providerId && this.providersJackpots[args.providerId].jackpotType === JackpotTypeEnum.STATIC) {
    //   const amount = items.get(args.providerId) || 0;
    //   this.jackpot.set(args.providerId, amount);
    //   return;
    // }

    for (const [providerId, amount] of items.entries()) {
      //TODO: Uncomment this block if you want to handle static jackpots separately
      // if (this.providersJackpots[providerId].jackpotType === JackpotTypeEnum.STATIC) {
      //   const amount = items.get(providerId) || 0;
      //   this.jackpot.set(providerId, amount);
      //   continue;
      // }

      if (RewardsStore.timeOutRef.has(providerId)) {
        const timeoutRef = RewardsStore.timeOutRef.get(providerId);
        clearTimeout(timeoutRef);
        RewardsStore.timeOutRef.delete(providerId);
      }
      if (RewardsStore.secIntervalRef.has(providerId)) {
        const secIntervalRef = RewardsStore.secIntervalRef.get(providerId);
        clearInterval(secIntervalRef);
        RewardsStore.secIntervalRef.delete(providerId);
      }
      if (RewardsStore.intervalRef.has(providerId)) {
        const intervalRef = RewardsStore.intervalRef.get(providerId);
        clearInterval(intervalRef);
        RewardsStore.intervalRef.delete(providerId);
      }

      if (!amount || amount <= 0) {
        this.jackpot.set(providerId, 0);
        continue;
      }

      const max = amount;
      const firstPhase = max * 0.95;
      const secondPhase = max * 0.05;

      this.startFastJackpotCounter(providerId, firstPhase);

      const durationSec = 180;
      const intervalTime = 60;
      const steps = (durationSec * 1000) / intervalTime;
      let currentStep = 0;
      this.jackpot.set(providerId, 0);
      RewardsStore.intervalRef.set(
        providerId,
        setInterval(() => {
          currentStep += 1;
          this.setCounter(providerId, currentStep, steps, secondPhase, () => {
            RewardsStore.timeOutRef.set(
              providerId,
              setTimeout(() => {
                this.startJackpotCounter({ ...args });
              }, 2000),
            );
          });
        }, intervalTime),
      );
    }
  }

  private easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3);

  @action
  private setCounter(providerId: number, currentStep: number, steps: number, max: number, onEnd: () => void) {
    const t = currentStep / steps; // Normalized time
    const next = this.easeOutCubic(t) * max; // Eased value
    if (currentStep >= steps) {
      if (RewardsStore.intervalRef.has(providerId)) {
        const intervalRef = RewardsStore.intervalRef.get(providerId);
        clearInterval(intervalRef);
        RewardsStore.intervalRef.delete(providerId);
      }
      this.jackpot.set(providerId, max);
      onEnd();
    } else {
      this.jackpot.set(providerId, next.toFixed(2));
    }
  }

  @action
  private startFastJackpotCounter(providerId: number, max: number) {
    if (RewardsStore.secIntervalRef.has(providerId)) {
      const secIntervalRef = RewardsStore.secIntervalRef.get(providerId);
      clearInterval(secIntervalRef);
      RewardsStore.secIntervalRef.delete(providerId);
    }
    const durationSec = 5;
    const intervalTime = 60;
    const steps = (durationSec * 1000) / intervalTime;
    this.fastJackpot.set(providerId, 0);
    let currentStep = 0;
    RewardsStore.secIntervalRef.set(
      providerId,
      setInterval(() => {
        currentStep += 1;
        this.setFastCounter(providerId, currentStep, steps, max);
      }, intervalTime),
    );
  }

  @action
  private setFastCounter(providerId: number, currentStep: number, steps: number, max: number, onEnd?: () => void) {
    const t = currentStep / steps; // Normalized time
    const next = this.easeOutCubic(t) * max; // Eased value
    if (currentStep >= steps) {
      if (RewardsStore.secIntervalRef.has(providerId)) {
        const secIntervalRef = RewardsStore.secIntervalRef.get(providerId);
        clearInterval(secIntervalRef);
        RewardsStore.secIntervalRef.delete(providerId);
      }
      this.fastJackpot.set(providerId, max);
      onEnd?.();
    } else {
      this.fastJackpot.set(providerId, next.toFixed(2));
    }
  }

  @action
  private setDailyBonusFetched() {
    this.isDailyBonusFetched = true;
  }

  @action
  public setWelcomeBonus(data: { gcAmount: number; scAmount: number }) {
    this.welcomeBonus = data;
  }

  @action
  private setDailyBonus(data: DailyBonusData | null) {
    if (!data) {
      this.resetDailyBonus();
      return;
    }
    this.dailyBonusesList = data?.bonusesList.filter((f) => f.type === 'daily');
    this.monthlyBonusesList = data?.bonusesList.filter((f) => f.type === 'monthly');
    this.dailyBonus = data;
    // The server sends a *remaining* duration, but we must keep an absolute deadline: a
    // relative counter that we decrement ourselves silently desyncs from real time whenever
    // the tab is backgrounded (mobile Safari suspends timers), leaving the player looking at
    // a frozen "next bonus in 06:53:41" while their claim window quietly expires.
    this.nextBonusAt = typeof data.timeUntilNextBonusMS === 'number' ? Date.now() + data.timeUntilNextBonusMS : null;
  }

  @action resetDailyBonus() {
    this.dailyBonus = null;
    this.dailyBonusesList = [];
    this.monthlyBonusesList = [];
    this.nextBonusAt = null;
  }

  // Absolute wall-clock instant the next bonus unlocks. Not observable: every read is paired
  // with a write to the observable `dailyBonus.timeUntilNextBonusMS` that the views render.
  private nextBonusAt: number | null = null;

  // The deadline we have already refetched for, so a server response that still reports
  // "not entitled, 0ms left" cannot turn this into one request per second.
  private expiryRefetchedFor: number | null = null;

  @action
  public countDownBonusTime() {
    if (!this.dailyBonus || this.nextBonusAt === null) {
      return;
    }

    // Recomputed from the clock rather than decremented, so returning to a suspended tab
    // shows the true remaining time instead of resuming from where the timer stalled.
    const remaining = Math.max(0, this.nextBonusAt - Date.now());
    this.dailyBonus.timeUntilNextBonusMS = remaining;

    // Hitting zero means our cached snapshot is now stale by definition — the player is
    // very likely entitled. Re-ask the server instead of sitting on a dead countdown.
    if (remaining === 0 && !this.dailyBonus.isEntitledToBonus && this.expiryRefetchedFor !== this.nextBonusAt) {
      this.expiryRefetchedFor = this.nextBonusAt;
      void this.getDailyBonus();
    }
  }

  /**
   * Refetch when a stale snapshot could be on screen. The countdown only self-heals while a
   * tick is running (i.e. the bonus dialog is mounted); a tab parked on the lobby for hours
   * has no ticker at all, so foregrounding is our other chance to notice the day rolled over.
   *
   * Deliberately narrow: this fires on every foreground, and /v2/user/daily-bonus is several
   * queries deep, so it only refetches when the cached snapshot is *provably* wrong rather
   * than merely old. In practice that is at most once per bonus day per player.
   */
  public async refreshDailyBonusIfStale(minIntervalMs = 60_000) {
    if (!this.isDailyBonusFetched || this.lastDailyBonusFetchAt === null) {
      return;
    }
    // Already claimable: the dialog is showing the correct, actionable state. Nothing to fix.
    if (this.dailyBonus?.isEntitledToBonus) {
      return;
    }
    // Not yet past its own deadline, so the snapshot is still accurate.
    if (this.nextBonusAt === null || Date.now() < this.nextBonusAt) {
      return;
    }
    // Floor on frequency, in case the server keeps handing back an elapsed deadline.
    if (Date.now() - this.lastDailyBonusFetchAt < minIntervalMs) {
      return;
    }
    await this.getDailyBonus();
  }

  private lastDailyBonusFetchAt: number | null = null;

  public async getDailyBonus() {
    try {
      const response = await getRequest<ApiResponse<DailyBonusData>>('/v2/user/daily-bonus');

      this.setDailyBonusFetched();
      this.lastDailyBonusFetchAt = Date.now();
      if (response.data.success) {
        const dailyBonusResult = response.data.data;
        this.setDailyBonus(dailyBonusResult);
        if (!dailyBonusResult?.isEntitledToBonus) {
          this.setDailyBonusCompleted();
        }
        return dailyBonusResult?.isEntitledToBonus;
      } else {
        this.setDailyBonusCompleted();
      }
    } catch (error) {
      devError('Error fetching daily bonus', error);
      // Stamped on failure too, so a persistently failing endpoint backs off instead of
      // being retried on every foreground.
      this.lastDailyBonusFetchAt = Date.now();
      this.setDailyBonusCompleted();
    }
  }

  public fetchWelcomeBonus = singleFlight(async () => {
    this.setWelcomeBonusLoading(true);
    try {
      const response = await getRequest<
        ApiResponse<{
          isActive: boolean;
          gcAmount: number;
          scAmount: number;
        }>
      >('user/welcome-bonus');
      const bonus = get(response, 'data.data');

      // if welcome bonus is enabled on the backend, set the amount
      if (bonus?.isActive) {
        this.setWelcomeBonus({
          gcAmount: bonus.gcAmount,
          scAmount: bonus.scAmount,
        });
      }
    } finally {
      this.setWelcomeBonusLoading(false);
    }
  });

  public async claimDailyBonus() {
    const ret = await postRequest<{
      data: {
        sc_amount: number;
        gc_amount: number;
        dailyPackageData: BundleItem[] | null;
        monthlyPackageData: BundleItem[] | null;
      };
    }>('/v2/user/daily-bonus');
    void this.getDailyBonus();
    return ret;
  }

  public async claimDailyBonusV2() {
    const ret = await postRequest<{
      data: ClaimDailyBonusResponseData;
    }>('/v2/user/claim-daily-bonus');
    void this.getDailyBonus();
    return ret;
  }

  private async subscribeToSocket(message: any) {
    if (message?.data) {
      await this.fetchInviteBonusNotification();
    }
  }
}
